/**
 * Provenance and status badges for the requirements register.
 *
 * The backend's verification ladder is UNVERIFIED → INFERRED →
 * DOCUMENT_CITED → HUMAN_VERIFIED (+ CONFLICTING / SUPERSEDED as trouble
 * states). The UI presents these truthfully: a citation is EVIDENCED, an
 * inference is an ASSUMPTION, and an assumption is never dressed up as a
 * contractual requirement.
 */
import { Badge } from "../ui/badge";
import {
  FileCheck,
  ShieldCheck,
  AlertTriangle,
  CircleDashed,
  GitPullRequestClosed,
  Flame,
} from "lucide-react";

const PROVENANCE = {
  DOCUMENT_CITED: {
    label: "EVIDENCED",
    title: "Cited to a source document (revision, page, and quotation on record).",
    className: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30",
    Icon: FileCheck,
  },
  HUMAN_VERIFIED: {
    label: "OPERATOR VERIFIED",
    title: "A named operator verified this against the source evidence.",
    className: "bg-green-500/20 text-green-400 border-green-500/30",
    Icon: ShieldCheck,
  },
  INFERRED: {
    label: "ASSUMPTION",
    title:
      "Inferred, not evidenced. Do not treat as a contractual requirement until it is cited or verified.",
    className: "bg-amber-500/20 text-amber-400 border-amber-500/30",
    Icon: AlertTriangle,
  },
  UNVERIFIED: {
    label: "UNVERIFIED",
    title: "Recorded without evidence. Provenance unknown.",
    className: "bg-muted text-muted-foreground border-border",
    Icon: CircleDashed,
  },
  /**
   * A person typed this in — through the register or through an import.
   * Its provenance is KNOWN and it is weak: honest, and not a citation. It is
   * deliberately not UNVERIFIED, which means "we do not know where this came
   * from"; here we know exactly where it came from.
   */
  OPERATOR_ENTERED: {
    label: "ENTERED BY HAND",
    title:
      "A person entered this directly. Honest provenance, but not evidence — it reports, and it does not enforce until a document or a verification elevates it.",
    className: "bg-amber-500/20 text-amber-400 border-amber-500/30",
    Icon: AlertTriangle,
  },
  CONFLICTING: {
    label: "CONFLICT",
    title:
      "Source statements disagree. The system keeps both and picks no winner — resolve it explicitly.",
    className: "bg-red-500/20 text-red-400 border-red-500/30",
    Icon: Flame,
  },
  SUPERSEDED: {
    label: "SUPERSEDED",
    title: "Replaced by a later revision. Kept on record; no longer current.",
    className: "bg-slate-500/20 text-slate-400 border-slate-500/30",
    Icon: GitPullRequestClosed,
  },
};

export const ProvenanceBadge = ({ verificationStatus, className = "" }) => {
  const spec = PROVENANCE[verificationStatus] || PROVENANCE.UNVERIFIED;
  const { Icon } = spec;
  return (
    <Badge
      variant="outline"
      title={spec.title}
      className={`${spec.className} gap-1 font-medium ${className}`}
    >
      <Icon className="w-3 h-3" />
      {spec.label}
    </Badge>
  );
};

const CONFIDENCE = {
  HIGH: "bg-emerald-500/15 text-emerald-400 border-emerald-500/25",
  MEDIUM: "bg-amber-500/15 text-amber-400 border-amber-500/25",
  LOW: "bg-orange-500/15 text-orange-400 border-orange-500/25",
  UNKNOWN: "bg-muted text-muted-foreground border-border",
};

export const ConfidenceBadge = ({ level }) => {
  if (!level) return null;
  return (
    <Badge
      variant="outline"
      title={`Evidence confidence: ${level}`}
      className={`${CONFIDENCE[level] || CONFIDENCE.UNKNOWN} font-normal text-[10px] px-1.5`}
    >
      {level}
    </Badge>
  );
};

/** Requirement obligation status — 12 backend states grouped by meaning. */
const REQUIREMENT_STATUS = {
  IDENTIFIED: "bg-blue-500/20 text-blue-400 border-blue-500/30",
  PREPARING: "bg-blue-500/20 text-blue-400 border-blue-500/30",
  SUBMITTED: "bg-violet-500/20 text-violet-400 border-violet-500/30",
  UNDER_REVIEW: "bg-violet-500/20 text-violet-400 border-violet-500/30",
  RETURNED_FOR_REVISION: "bg-amber-500/20 text-amber-400 border-amber-500/30",
  RESUBMITTED: "bg-violet-500/20 text-violet-400 border-violet-500/30",
  APPROVED: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30",
  APPROVED_AS_NOTED: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30",
  REJECTED: "bg-red-500/20 text-red-400 border-red-500/30",
  WAIVED: "bg-slate-500/20 text-slate-400 border-slate-500/30",
  SUPERSEDED: "bg-slate-500/20 text-slate-400 border-slate-500/30",
  CANCELLED: "bg-slate-500/20 text-slate-400 border-slate-500/30",
};

export const RequirementStatusBadge = ({ status }) => (
  <Badge
    variant="outline"
    className={`${REQUIREMENT_STATUS[status] || "bg-muted text-muted-foreground border-border"} font-normal`}
  >
    {(status || "UNKNOWN").replaceAll("_", " ")}
  </Badge>
);
